import {
  NOTIFICATION_CATEGORIES,
  type NotificationCategory,
  type NotificationContent,
} from "@loot-os/api-contract";
import { sql } from "drizzle-orm";
import {
  index,
  integer,
  jsonb,
  pgTable,
  primaryKey,
  text,
  timestamp,
  uniqueIndex,
} from "drizzle-orm/pg-core";

import { endUsers, environments } from "./app.js";
import { tenant } from "./auth.js";

// ---- notifications ---------------------------------------------------------------------------
// What the storefront's bell lists (docs/notifications.md). A system notification stores its type
// and the raw values it renders from; the text is rendered when read, in the environment's current
// template, words and currency. A marketing notification stores the campaign's content as sent.

export const NOTIFICATION_CAMPAIGN_AUDIENCES = ["all", "list"] as const;
export type NotificationCampaignAudience = (typeof NOTIFICATION_CAMPAIGN_AUDIENCES)[number];

export const NOTIFICATION_CAMPAIGN_STATUSES = ["scheduled", "sent"] as const;
export type NotificationCampaignStatus = (typeof NOTIFICATION_CAMPAIGN_STATUSES)[number];

/**
 * A marketing notification composed in the dashboard, sent to every unlocked end user of the
 * environment or to an uploaded list, now or at `scheduled_at`. Writer: tenant admin
 * (`notifications.createCampaign` / `updateCampaign` / `deleteCampaign`, while `scheduled`) and the
 * dispatch cron (`status`, `sent_at`, `delivered_count`). Whole row is safe to return to that
 * tenant's admins; `content` is what shoppers see.
 */
export const notificationCampaigns = pgTable(
  "notification_campaigns",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    /** Title, body, icon, action and banner (`NotificationContent`); read through `parseNotificationContent`. */
    content: jsonb("content").$type<NotificationContent>().notNull(),
    audience: text("audience", { enum: NOTIFICATION_CAMPAIGN_AUDIENCES }).notNull(),
    status: text("status", { enum: NOTIFICATION_CAMPAIGN_STATUSES }).notNull(),
    /** When it goes out; a send-now campaign is scheduled for its own creation. */
    scheduledAt: timestamp("scheduled_at", { withTimezone: true }).notNull(),
    sentAt: timestamp("sent_at", { withTimezone: true }),
    /** How many notifications the send wrote: unlocked recipients who had not opted out. */
    deliveredCount: integer("delivered_count"),
    /** The dashboard user who composed it. */
    createdBy: text("created_by").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index("notification_campaigns_env_idx").on(t.environmentId, t.createdAt),
    index("notification_campaigns_due_idx").on(t.scheduledAt).where(sql`${t.status} = 'scheduled'`),
  ],
);

/**
 * The uploaded list of a `list` campaign, resolved to end users when it is saved. Writer: tenant
 * admin, through the campaign's create and update. Safe to return to that tenant's admins.
 */
export const notificationCampaignRecipients = pgTable(
  "notification_campaign_recipients",
  {
    campaignId: text("campaign_id")
      .notNull()
      .references(() => notificationCampaigns.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
  },
  (t) => [
    primaryKey({ columns: [t.campaignId, t.endUserId] }),
    // The primary key leads with the campaign, so an end user's delete cascades through this.
    index("notification_campaign_recipients_end_user_idx").on(t.endUserId),
  ],
);

/**
 * One notification in one shopper's bell. Writer: the platform (`deliverNotification`, from the
 * features that fire system types, and the campaign dispatch) and the shopper, who sets
 * `read_at` (`POST /v1/me/notifications/read`). `data` is staff-chosen values only, never a lock
 * reason, a fraud verdict or a staff note. Safe to return to that shopper, rendered.
 */
export const notifications = pgTable(
  "notifications",
  {
    id: text("id").primaryKey(), // ulid
    tenantId: text("tenant_id")
      .notNull()
      .references(() => tenant.id, { onDelete: "cascade" }),
    environmentId: text("environment_id")
      .notNull()
      .references(() => environments.id, { onDelete: "cascade" }),
    endUserId: text("end_user_id")
      .notNull()
      .references(() => endUsers.id, { onDelete: "cascade" }),
    category: text("category", { enum: NOTIFICATION_CATEGORIES })
      .$type<NotificationCategory>()
      .notNull(),
    /** A system type (`NOTIFICATION_TYPES`), or `marketing`. Text, so a type needs no migration. */
    type: text("type").notNull(),
    /** A system type's raw values, or a marketing notification's `NotificationContent`. */
    data: jsonb("data").$type<Record<string, unknown>>().notNull().default({}),
    campaignId: text("campaign_id").references(() => notificationCampaigns.id, {
      onDelete: "set null",
    }),
    /** Names the event (`order:{id}:shipped`), so a retried hook or a redelivered webhook writes once. */
    dedupeKey: text("dedupe_key"),
    readAt: timestamp("read_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    /** Past this the bell no longer lists it and the daily job deletes it. */
    expiresAt: timestamp("expires_at", { withTimezone: true }).notNull(),
  },
  (t) => [
    index("notifications_end_user_idx").on(t.endUserId, t.id),
    index("notifications_unread_idx").on(t.endUserId).where(sql`${t.readAt} IS NULL`),
    uniqueIndex("notifications_dedupe_idx")
      .on(t.endUserId, t.dedupeKey)
      .where(sql`${t.dedupeKey} IS NOT NULL`),
    index("notifications_expires_idx").on(t.expiresAt),
    index("notifications_campaign_idx").on(t.campaignId),
  ],
);

/**
 * The campaign dispatcher's own state, one row: which run holds it and until when (so runs inside
 * their timeout, in any Lambda environment, never walk the queue together; sends stay single
 * through each campaign's `SKIP LOCKED` either way), and the place the running or last run
 * reached, for the next to carry on from (null after a full pass). Writer: the dispatcher alone
 * (`runNotificationDispatch`); never tenant-facing.
 */
export const notificationDispatchState = pgTable("notification_dispatch_state", {
  id: text("id").primaryKey(),
  leaseHolder: text("lease_holder"),
  leaseExpiresAt: timestamp("lease_expires_at", { withTimezone: true }),
  resumeAfterScheduledAt: timestamp("resume_after_scheduled_at", { withTimezone: true }),
  resumeAfterId: text("resume_after_id"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
});
